import { useEffect, useState } from "react";
import {
  etkinlikYanitiSemasi,
  mentorumEtkinlikOkumaSemasi,
  type MentorumEtkinlikOkumaYaniti,
} from "@koclukpanel/contracts";
import { Buton, Iskelet, Kart, MetinAlani, OnayKutusu } from "@koclukpanel/ui";
import { api, ApiHatasi } from "./api";
import {
  GonderilmisYanit,
  mentorumHataMetni,
  mentorumKapiHatasi,
  type MentorumKonusu,
  type MentorumYaniti,
} from "./Mentorum";

export function MentorumKonu({
  konu,
  onKapiHatasi,
}: {
  konu: MentorumKonusu;
  onKapiHatasi?: (hata: unknown) => void;
}) {
  const [etkinlikler, setEtkinlikler] = useState<MentorumEtkinlikOkumaYaniti | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [medya, setMedya] = useState<{ id: string; url: string; kind: "VIDEO" | "AUDIO" } | null>(
    null,
  );
  const [islem, setIslem] = useState(false);
  const [yenileme, setYenileme] = useState(0);
  useEffect(() => {
    const iptal = new AbortController();
    void api(`/mentorum/icerik/${encodeURIComponent(konu.id)}/etkinlikler`, {
      signal: iptal.signal,
      taze: true,
    })
      .then((yanit) => {
        if (!iptal.signal.aborted) setEtkinlikler(mentorumEtkinlikOkumaSemasi.parse(yanit));
      })
      .catch((sorun: unknown) => {
        if (!iptal.signal.aborted) {
          setHata(mentorumHataMetni(sorun));
          if (mentorumKapiHatasi(sorun)) onKapiHatasi?.(sorun);
        }
      });
    return () => iptal.abort();
  }, [konu.id, onKapiHatasi, yenileme]);
  const medyaAc = async (secili: MentorumKonusu["media"][number]) => {
    setMedya(null);
    setHata(null);
    setIslem(true);
    try {
      // F-MNT-07/08 — her açılış yeni kısa ömürlü bağlantı ister; konu kilidini sunucu denetler.
      const baglanti = await api<{ url: string }>(
        `/mentorum/icerik/${encodeURIComponent(konu.id)}/baglanti`,
        { sorgu: { medyaId: secili.id }, taze: true },
      );
      setMedya({ id: secili.id, url: baglanti.url, kind: secili.kind });
      // F-MNT-08 — medya açılışı konuyu sunucuda başlatır; etkinlikler bundan sonra yeniden sorulur.
      setYenileme((sayi) => sayi + 1);
    } catch (sorun) {
      setHata(mentorumHataMetni(sorun));
      if (mentorumKapiHatasi(sorun)) onKapiHatasi?.(sorun);
    } finally {
      setIslem(false);
    }
  };
  return (
    <section className="flex min-w-0 flex-col gap-3 [overflow-wrap:anywhere]">
      <h2 className="text-lg font-semibold">{konu.title}</h2>
      <p>
        {konu.authorName} · {konu.authorTitle}
      </p>
      {konu.description && <p className="whitespace-pre-wrap">{konu.description}</p>}
      {hata && <p role="alert">{hata}</p>}
      {konu.media.map((kayit) => (
        <Buton
          key={kayit.id}
          gorunum="ikincil"
          disabled={islem}
          onClick={() => void medyaAc(kayit)}
        >
          {kayit.kind === "VIDEO" ? "Videoyu aç" : "Ses kaydını aç"}: {kayit.title}
        </Buton>
      ))}
      {medya &&
        (medya.kind === "VIDEO" ? (
          <video
            key={medya.url}
            aria-label="Konu videosu"
            src={medya.url}
            controls
            controlsList="nodownload"
            playsInline
            preload="none"
            className="rounded-alan w-full min-w-0"
            onError={() => {
              setMedya(null);
              setHata("Video açılamadı. Yeniden açarak bağlantıyı yenileyebilirsin.");
            }}
          />
        ) : (
          <audio
            key={medya.url}
            aria-label="Konu ses kaydı"
            src={medya.url}
            controls
            controlsList="nodownload"
            preload="none"
            className="w-full min-w-0"
            onError={() => {
              setMedya(null);
              setHata("Ses kaydı açılamadı. Yeniden açarak bağlantıyı yenileyebilirsin.");
            }}
          />
        ))}
      {!etkinlikler ? (
        <>
          {!hata && <Iskelet satir={2} />}
          {hata && (
            <Buton
              onClick={() => {
                setHata(null);
                setYenileme((sayi) => sayi + 1);
              }}
            >
              Tekrar dene
            </Buton>
          )}
        </>
      ) : etkinlikler.length === 0 ? (
        <p>Bu konuda henüz etkinlik yok.</p>
      ) : (
        etkinlikler.map((etkinlik) => (
          <EtkinlikFormu key={etkinlik.id} etkinlik={etkinlik} onKapiHatasi={onKapiHatasi} />
        ))
      )}
    </section>
  );
}

function EtkinlikFormu({
  etkinlik,
  onKapiHatasi,
}: {
  etkinlik: MentorumEtkinlikOkumaYaniti[number];
  onKapiHatasi?: (hata: unknown) => void;
}) {
  const [yanit, setYanit] = useState<MentorumYaniti | null | undefined>(undefined);
  const [secimler, setSecimler] = useState<string[]>([]);
  const [metin, setMetin] = useState("");
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [islem, setIslem] = useState(false);
  const [yenileme, setYenileme] = useState(0);
  const yol = `/mentorum/etkinlik/${encodeURIComponent(etkinlik.id)}/yanit`;
  useEffect(() => {
    const iptal = new AbortController();
    void api<MentorumYaniti[]>(yol, { signal: iptal.signal, taze: true }).then(
      (yanitlar) => {
        if (iptal.signal.aborted) return;
        const kayit = yanitlar[0] ?? null;
        setYanit(kayit);
        setSecimler(kayit?.selectedOptionIds ?? []);
        setMetin(kayit?.textAnswer ?? "");
      },
      (sorun: unknown) => {
        if (!iptal.signal.aborted) {
          setHata(mentorumHataMetni(sorun));
          if (mentorumKapiHatasi(sorun)) onKapiHatasi?.(sorun);
        }
      },
    );
    return () => iptal.abort();
  }, [yol, onKapiHatasi, yenileme]);
  const kaydet = async (gonder: boolean) => {
    const sonuc = etkinlikYanitiSemasi.safeParse({
      selectedOptionIds: secimler,
      textAnswer: metin,
      gonder,
    });
    if (!sonuc.success) {
      setHata(sonuc.error.issues[0]?.message ?? "Yanıtını kontrol et.");
      return;
    }
    setIslem(true);
    setHata(null);
    setBilgi(null);
    try {
      // F-MNT-09 — yazım yanıtı içerik taşımaz; gönderimden sonra sunucunun değişmez kopyası yeniden okunur.
      await api(yol, { yontem: "POST", govde: sonuc.data });
      setBilgi(gonder ? "Yanıtın gönderildi." : "Taslağın kaydedildi.");
      if (gonder) {
        setYanit(undefined);
        setSecimler([]);
        setMetin("");
        setYenileme((sayi) => sayi + 1);
      }
    } catch (sorun) {
      setHata(mentorumHataMetni(sorun));
      if (mentorumKapiHatasi(sorun)) onKapiHatasi?.(sorun);
      if (
        sorun instanceof ApiHatasi &&
        (sorun.kod === "YANIT_GONDERILMIS" || sorun.kod === "YANIT_DEGISTI")
      ) {
        setYanit(undefined);
        setSecimler([]);
        setMetin("");
        setYenileme((sayi) => sayi + 1);
      }
    } finally {
      setIslem(false);
    }
  };
  if (yanit?.status === "SUBMITTED") return <GonderilmisYanit yanit={yanit} />;
  if (yanit === undefined)
    return (
      <Kart baslik={etkinlik.prompt}>
        {hata ? (
          <>
            <p role="alert">{hata}</p>
            <Buton
              onClick={() => {
                setHata(null);
                setYenileme((sayi) => sayi + 1);
              }}
            >
              Tekrar dene
            </Buton>
          </>
        ) : (
          <Iskelet satir={2} />
        )}
      </Kart>
    );
  return (
    <Kart baslik={etkinlik.prompt} className="min-w-0">
      <fieldset disabled={islem} className="min-w-0">
        <legend>Yanıtını seç</legend>
        {etkinlik.options.map((secenek) => (
          <OnayKutusu
            key={secenek.id}
            etiket={secenek.text}
            checked={secimler.includes(secenek.id)}
            onChange={(olay) =>
              setSecimler((eski) =>
                olay.target.checked
                  ? [...eski, secenek.id]
                  : eski.filter((kimlik) => kimlik !== secenek.id),
              )
            }
          />
        ))}
        {etkinlik.allowsText && (
          <MetinAlani
            etiket={etkinlik.textPrompt ?? "Yanıtın"}
            value={metin}
            onChange={(olay) => setMetin(olay.target.value)}
          />
        )}
      </fieldset>
      {hata && (
        <p role="alert" className="mt-3">
          {hata}
        </p>
      )}
      {bilgi && (
        <p role="status" className="mt-3">
          {bilgi}
        </p>
      )}
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <Buton gorunum="ikincil" disabled={islem} onClick={() => void kaydet(false)}>
          Taslağı kaydet
        </Buton>
        <Buton disabled={islem} onClick={() => void kaydet(true)}>
          Yanıtı gönder
        </Buton>
      </div>
    </Kart>
  );
}
